// GPH Expert Intelligence Core — Training Coherence Guard (PDS §12.1).
// Trava real antes de entregar treino: o drill precisa pertencer a uma
// categoria permitida pelo contexto (sintoma diagnosticado ou situação).
// Exemplo do PDS: "mira sobe no spray" NUNCA pode gerar treino de cover quebrado.

import { findDrillByGoal, GPH_DRILLS } from '../../data/gphDrillCatalog';

// Categoria(s) de cada drill do catálogo. Conhecimento de coerência vive aqui,
// não no catálogo (que permanece dados puros).
export const DRILL_CATEGORY_MAP = {
  strafe_short_trade: ['close_combat', 'movement'],
  timing_break_vs_rush: ['timing', 'angle_break'],
  stairs_push_defense: ['angle_break', 'cover'],
  reset_lost_trade: ['disengage', 'cover'],
  isolate_1v1_vs_squad: ['duel_isolation', 'repositioning'],
  smoke_reposition: ['utility', 'repositioning'],
  short_repeek_punish: ['angle_break', 'timing'],
  pinch_escape: ['disengage', 'repositioning'],
  broken_cover_defense: ['cover', 'repositioning'],
  house_entry_sound_bait: ['timing', 'close_combat'],
  vertical_recoil_control: ['recoil_control', 'spray_tracking', 'gyro_ads'],
  spray_tracking_moving: ['spray_tracking', 'tracking'],
  flick_two_targets: ['flick_control', 'tracking'],
  burst_control_distance: ['burst_control', 'recoil_control', 'distance_control']
};

export const getDrillCategories = (drillId) => DRILL_CATEGORY_MAP[drillId] || [];

// Sintoma de sensibilidade → categorias de treino permitidas (PDS §12.1).
export const SYMPTOM_ALLOWED_CATEGORIES = {
  sobe: ['recoil_control', 'spray_tracking', 'burst_control', 'gyro_ads', 'distance_control'],
  cai: ['recoil_control', 'burst_control', 'gyro_ads', 'distance_control'],
  treme: ['tracking', 'spray_tracking', 'gyro_ads'],
  passa: ['flick_control', 'tracking'],
  nao_acompanha: ['tracking', 'spray_tracking'],
  nao_sei: ['recoil_control', 'tracking', 'spray_tracking', 'flick_control', 'burst_control', 'gyro_ads']
};

// Situação de combate → categorias permitidas (PDS §12.1, contexto 1v4).
export const SITUATION_ALLOWED_CATEGORIES = {
  clutch_1v4: ['duel_isolation', 'repositioning', 'angle_break', 'cover', 'utility', 'reload_discipline', 'timing', 'disengage'],
  rush_defense: ['timing', 'angle_break', 'cover', 'close_combat'],
  pinch: ['disengage', 'repositioning', 'utility']
};

// Sintoma → termo de busca no catálogo (substitui o hardcode 'strafe').
export const SYMPTOM_DRILL_GOAL = {
  sobe: 'controle de spray',
  cai: 'burst controle vertical',
  treme: 'tracking',
  passa: 'flick',
  nao_acompanha: 'tracking acompanhamento',
  nao_sei: 'controle de spray'
};

export const mapSymptomToDrillGoal = (symptom) =>
  SYMPTOM_DRILL_GOAL[symptom] || SYMPTOM_DRILL_GOAL.nao_sei;

// Objetivo textual → categorias permitidas (1v4 → isolamento de duelos etc.).
export const getObjectiveAllowedCategories = (normalizedObjective = '') => {
  if (/1v4|1x4|contra squad|sozinho contra|clutch/.test(normalizedObjective)) {
    return SITUATION_ALLOWED_CATEGORIES.clutch_1v4;
  }
  if (/rush/.test(normalizedObjective)) return SITUATION_ALLOWED_CATEGORIES.rush_defense;
  if (/pincamento|pinçamento|cercado/.test(normalizedObjective)) return SITUATION_ALLOWED_CATEGORIES.pinch;
  if (/spray|recoil|mira sobe/.test(normalizedObjective)) return SYMPTOM_ALLOWED_CATEGORIES.sobe;
  return null;
};

// Objetivo textual → termo de busca (1v4 → isolamento; rush → defesa etc.).
export const mapObjectiveToDrillGoal = (normalizedObjective = '') => {
  if (/1v4|1x4|contra squad|sozinho contra|clutch/.test(normalizedObjective)) return 'isolar squad 1v4';
  if (/rush/.test(normalizedObjective)) return 'rush defesa';
  if (/pincamento|pinçamento|cercado/.test(normalizedObjective)) return 'pincamento escape';
  if (/spray|recoil|mira sobe/.test(normalizedObjective)) return 'controle de spray';
  return normalizedObjective;
};

// Valida coerência drill × contexto. allowedCategories vem do sintoma,
// da situação ou é null (sem restrição — pedido direto do usuário).
export const validateTrainingCoherence = (allowedCategories, drill) => {
  if (!drill) {
    return { isCoherent: false, reason: 'drill_not_found', action: 'ask_goal' };
  }
  if (!allowedCategories || allowedCategories.length === 0) {
    return { isCoherent: true, reason: 'no_context_restriction', action: 'ok' };
  }
  const categories = getDrillCategories(drill.drillId);
  const coherent = categories.some((cat) => allowedCategories.includes(cat));
  return coherent
    ? { isCoherent: true, reason: 'category_match', action: 'ok' }
    : {
      isCoherent: false,
      reason: `drill '${drill.drillId}' (${categories.join(',')}) fora das categorias permitidas [${allowedCategories.join(',')}]`,
      action: 'regenerate'
    };
};

// Busca um drill coerente com o contexto: tenta o goal pedido; se o resultado
// for incoerente, regenera buscando dentro das categorias permitidas.
export const findCoherentDrill = (goalOrText, allowedCategories = null) => {
  const candidate = findDrillByGoal(goalOrText);
  const check = validateTrainingCoherence(allowedCategories, candidate);
  if (check.isCoherent) {
    return { drill: candidate, regenerated: false, check };
  }
  // Regeneração: primeiro drill do catálogo dentro das categorias permitidas.
  const fallback = GPH_DRILLS.find((drill) =>
    getDrillCategories(drill.drillId).some((cat) => (allowedCategories || []).includes(cat))
  ) || null;
  return {
    drill: fallback,
    regenerated: true,
    check: fallback
      ? { isCoherent: true, reason: 'regenerated_within_allowed_categories', action: 'ok' }
      : { isCoherent: false, reason: 'no_coherent_drill_available', action: 'ask_goal' }
  };
};

// Guard de vídeo: capabilities de análise exigem vídeo real (PDS §12.2).
export const requiresVideo = (capabilityId) =>
  typeof capabilityId === 'string' && capabilityId.startsWith('mi_analysis');

const coherenceGuard = {
  mapSymptomToDrillGoal,
  mapObjectiveToDrillGoal,
  validateTrainingCoherence,
  findCoherentDrill,
  requiresVideo,
  getDrillCategories
};

export default coherenceGuard;
